<script setup lang="ts">
import { computed } from "vue"
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core"
import { ButtonSeverity, TuButton } from "@tinyurldev/tu-ui-vue"

import type { Subscription } from "@Modules/subscriptions/Data/Models/SubscriptionModel"

//
// Setup
const { smallerOrEqual } = useBreakpoints(breakpointsBootstrapV5)

//
// Props
interface Props {
  subscription: Subscription | null
  orgSlug: string | null
  loading: boolean
}
const props = defineProps<Props>()

//
// Computed
const isMobileView = smallerOrEqual("xl")

const hasActiveSubscription = computed((): boolean => {
  if (!props.subscription) {
    return false
  }

  // Only show cancel button if subscription is active and not free
  return props.subscription.isActive() && !props.subscription.isFree()
})

const cancellationCopy = computed((): string => {
  if (!props.subscription) {
    return ""
  }

  if (props.subscription.isPastDue()) {
    return "Canceling your subscription will convert you to a Free Plan. You may resume your old subscription or sign up for a new plan at a later date."
  }

  if (props.subscription.isTrialing()) {
    const trialDays = props.subscription.remainingTrialDays() ?? 0
    return `If you cancel, the benefits of your subscription will continue until your ${trialDays}-day free trial ends. You may resume your subscription at no extra cost until the end of your free trial.`
  }

  return "If you cancel, the benefits of your subscription will continue until your current billing period ends. You may resume your subscription at no extra cost until the end of the billing period."
})

const cancellationUrl = computed(() => {
  if (!props.orgSlug) {
    return {}
  }

  return {
    name: "org.settings.profile.subscription.cancel",
    params: {
      org: props.orgSlug,
    },
  }
})

//
// Exposed Test Vars
defineExpose({
  hasActiveSubscription,
  cancellationCopy,
})
</script>

<template>
  <div
    v-if="hasActiveSubscription"
    class="org-subscription-cancellation _cancellation"
    :class="{ 'org-subscription-cancellation--mobile': isMobileView }"
  >
    <p class="org-subscription-cancellation__copy _copy">
      {{ cancellationCopy }}
    </p>
    <TuButton
      class="org-subscription-cancellation__cta _cancel_button"
      as="router-link"
      label="Cancel Subscription"
      :severity="ButtonSeverity.ALERT"
      is-outlined
      :to="cancellationUrl"
      :is-disabled="loading"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;

.org-subscription-cancellation {
  $self: &;

  display: flex;
  align-items: flex-start;
  gap: spacing.$s;

  &__copy {
    margin-bottom: 0;
  }

  &__cta {
    white-space: nowrap;
  }

  &--mobile {
    flex-direction: column;

    #{$self}__cta {
      display: block;
      width: 100%;
    }
  }
}
</style>
